import axios from 'axios';
import { apiClient } from './apiClient';
import { getApiBaseUrl } from './apiConfig';

export type DocumentationVisibility = 'Public' | 'Private';
export type DocumentationPageStatus = 'Draft' | 'Published' | 'Archived' | 'Expired';
export type DocumentationContentFormat = 'Html' | 'Markdown';

export type DocumentationSpace = {
  id: string;
  name: string;
  description?: string | null;
  slug: string;
  visibility: string;
  shareToken?: string | null;
  salesProductId?: string | null;
  salesProductName?: string | null;
  sortOrder: number;
  pageCount: number;
  hasPublishedPages?: boolean;
  createdAtUtc: string;
  updatedAtUtc: string;
};

export type DocumentationSpaceListResult = {
  items: DocumentationSpace[];
  totalCount: number;
  page: number;
  pageSize: number;
};

export type DocumentationSpaceUpsert = {
  name: string;
  description?: string;
  visibility?: DocumentationVisibility;
  salesProductId?: string | null;
  sortOrder?: number;
};

export type DocumentationPageSummary = {
  id: string;
  title: string;
  slug: string;
};

export type DocumentationPageTreeNode = {
  id: string;
  title: string;
  slug: string;
  status: string;
  sortOrder: number;
  children: DocumentationPageTreeNode[];
};

export type DocumentationPage = {
  id: string;
  spaceId: string;
  title: string;
  slug: string;
  body: string;
  contentFormat: DocumentationContentFormat | string;
  parentPageId?: string | null;
  sortOrder: number;
  status: string;
  visibility: string;
  categoryId?: string | null;
  categoryName?: string | null;
  tags: string[];
  previousPageId?: string | null;
  nextPageId?: string | null;
  previousPage?: DocumentationPageSummary | null;
  nextPage?: DocumentationPageSummary | null;
  children: DocumentationPageSummary[];
  createdAtUtc: string;
  updatedAtUtc: string;
  publishedAtUtc?: string | null;
  expiresAtUtc?: string | null;
  workingVersionLabel?: string | null;
  publishedVersionLabel?: string | null;
  publishedTitle?: string | null;
  publishedBody?: string | null;
  hasUnpublishedChanges?: boolean;
  workingVersionId?: string | null;
  currentPublishedVersionId?: string | null;
};

export type DocumentationPageUpsert = {
  title: string;
  body?: string;
  contentFormat?: DocumentationContentFormat;
  parentPageId?: string | null;
  sortOrder?: number;
  previousPageId?: string | null;
  nextPageId?: string | null;
  status?: DocumentationPageStatus;
  visibility?: DocumentationVisibility;
  categoryId?: string | null;
  tagNames?: string[];
  expiresAtUtc?: string | null;
};

export type DocumentationPageMetadataUpsert = {
  visibility?: DocumentationVisibility;
  tagNames?: string[];
  previousPageId?: string | null;
  nextPageId?: string | null;
  categoryId?: string | null;
  parentPageId?: string | null;
  sortOrder?: number;
};

export type DocumentationPagePublish = {
  expiresAtUtc?: string | null;
};

export type DocumentationPageVersionSummary = {
  id: string;
  major: number;
  minor: number;
  label: string;
  kind: string;
  title: string;
  createdAtUtc: string;
  publishedAtUtc?: string | null;
  restoredFromVersionId?: string | null;
  isCurrentPublished: boolean;
  isWorking: boolean;
};

export type PublicDocumentationSpace = {
  name: string;
  description?: string | null;
  slug: string;
  pages: DocumentationPageTreeNode[];
  updatedAtUtc: string;
};

export type PublicDocumentationPage = {
  title: string;
  slug: string;
  body: string;
  contentFormat: DocumentationContentFormat | string;
  categoryName?: string | null;
  tags: string[];
  previousPage?: DocumentationPageSummary | null;
  nextPage?: DocumentationPageSummary | null;
  publishedAtUtc?: string | null;
  updatedAtUtc: string;
};

export type DocumentationApiTargets = {
  spacesBase: string;
  reportsBase: string;
  attachmentsBase: (pageId: string) => string;
};

const technologyDocumentationApiTargets: DocumentationApiTargets = {
  spacesBase: '/v1/technology/documentation/spaces',
  reportsBase: '/v1/technology/documentation/reports',
  attachmentsBase: (pageId) => `/v1/technology/documentation/pages/${pageId}/attachments`,
};

export const manualDocumentationApiTargets: DocumentationApiTargets = {
  spacesBase: '/v1/manual/spaces',
  reportsBase: '/v1/manual/reports',
  attachmentsBase: (pageId) => `/v1/manual/pages/${pageId}/attachments`,
};

export const releaseDocumentationApiTargets: DocumentationApiTargets = {
  spacesBase: '/v1/technology/deployments/documentation/spaces',
  reportsBase: '/v1/technology/deployments/documentation/reports',
  attachmentsBase: (pageId) =>
    `/v1/technology/deployments/documentation/pages/${pageId}/attachments`,
};

let activeDocumentationApiTargets = technologyDocumentationApiTargets;

export function setDocumentationApiTargets(targets: DocumentationApiTargets | null) {
  activeDocumentationApiTargets = targets ?? technologyDocumentationApiTargets;
}

function base() {
  return activeDocumentationApiTargets.spacesBase;
}

export function documentationReportsBase() {
  return activeDocumentationApiTargets.reportsBase;
}

export async function listDocumentationSpaces(params?: {
  search?: string;
  page?: number;
  pageSize?: number;
}): Promise<DocumentationSpaceListResult> {
  const response = await apiClient.get<DocumentationSpaceListResult>(base(), { params });
  return response.data;
}

export async function getDocumentationSpace(id: string): Promise<DocumentationSpace> {
  const response = await apiClient.get<DocumentationSpace>(`${base()}/${id}`);
  return response.data;
}

export async function createDocumentationSpace(
  payload: DocumentationSpaceUpsert,
): Promise<DocumentationSpace> {
  const response = await apiClient.post<DocumentationSpace>(base(), payload);
  return response.data;
}

export async function updateDocumentationSpace(
  id: string,
  payload: DocumentationSpaceUpsert,
): Promise<DocumentationSpace> {
  const response = await apiClient.put<DocumentationSpace>(`${base()}/${id}`, payload);
  return response.data;
}

export async function deleteDocumentationSpace(id: string): Promise<void> {
  await apiClient.delete(`${base()}/${id}`);
}

export async function getDocumentationPageTree(
  spaceId: string,
  includeDrafts = true,
): Promise<DocumentationPageTreeNode[]> {
  const response = await apiClient.get<DocumentationPageTreeNode[]>(`${base()}/${spaceId}/pages/tree`, {
    params: { includeDrafts },
  });
  return response.data;
}

export async function getDocumentationPage(spaceId: string, pageId: string): Promise<DocumentationPage> {
  const response = await apiClient.get<DocumentationPage>(`${base()}/${spaceId}/pages/${pageId}`);
  return response.data;
}

export async function createDocumentationPage(
  spaceId: string,
  payload: DocumentationPageUpsert,
): Promise<DocumentationPage> {
  const response = await apiClient.post<DocumentationPage>(`${base()}/${spaceId}/pages`, payload);
  return response.data;
}

export async function updateDocumentationPage(
  spaceId: string,
  pageId: string,
  payload: DocumentationPageUpsert,
): Promise<DocumentationPage> {
  const response = await apiClient.put<DocumentationPage>(`${base()}/${spaceId}/pages/${pageId}`, payload);
  return response.data;
}

export async function updateDocumentationPageMetadata(
  spaceId: string,
  pageId: string,
  payload: DocumentationPageMetadataUpsert,
): Promise<DocumentationPage> {
  const response = await apiClient.put<DocumentationPage>(
    `${base()}/${spaceId}/pages/${pageId}/metadata`,
    payload,
  );
  return response.data;
}

export async function beginEditDocumentationPage(
  spaceId: string,
  pageId: string,
): Promise<DocumentationPage> {
  const response = await apiClient.post<DocumentationPage>(
    `${base()}/${spaceId}/pages/${pageId}/begin-edit`,
  );
  return response.data;
}

export async function publishDocumentationPage(
  spaceId: string,
  pageId: string,
  payload: DocumentationPagePublish = {},
): Promise<DocumentationPage> {
  const response = await apiClient.post<DocumentationPage>(
    `${base()}/${spaceId}/pages/${pageId}/publish`,
    payload,
  );
  return response.data;
}

export async function discardDocumentationWorkingCopy(
  spaceId: string,
  pageId: string,
): Promise<DocumentationPage> {
  const response = await apiClient.post<DocumentationPage>(
    `${base()}/${spaceId}/pages/${pageId}/discard-working-copy`,
  );
  return response.data;
}

export async function listDocumentationPageVersions(
  spaceId: string,
  pageId: string,
): Promise<DocumentationPageVersionSummary[]> {
  const response = await apiClient.get<DocumentationPageVersionSummary[]>(
    `${base()}/${spaceId}/pages/${pageId}/versions`,
  );
  return response.data;
}

export async function restoreDocumentationPageVersion(
  spaceId: string,
  pageId: string,
  versionId: string,
): Promise<DocumentationPage> {
  const response = await apiClient.post<DocumentationPage>(
    `${base()}/${spaceId}/pages/${pageId}/versions/${versionId}/restore`,
  );
  return response.data;
}

export async function deleteDocumentationPage(spaceId: string, pageId: string): Promise<void> {
  await apiClient.delete(`${base()}/${spaceId}/pages/${pageId}`);
}

export async function fetchPublicDocumentationSpace(token: string): Promise<PublicDocumentationSpace> {
  const response = await axios.get<PublicDocumentationSpace>(
    `${getApiBaseUrl()}/v1/public/documentation/${encodeURIComponent(token)}`,
  );
  return response.data;
}

export async function fetchPublicDocumentationPage(
  token: string,
  pageSlug: string,
  options?: { countView?: boolean },
): Promise<PublicDocumentationPage> {
  const response = await axios.get<PublicDocumentationPage>(
    `${getApiBaseUrl()}/v1/public/documentation/${encodeURIComponent(token)}/pages/${encodeURIComponent(pageSlug)}`,
    {
      params: {
        countView: options?.countView ?? true,
      },
    },
  );
  return response.data;
}

export type DocumentationAttachment = {
  id: string;
  pageId: string;
  fileName: string;
  contentType: string;
  fileSizeBytes: number;
  fileExtension?: string | null;
  uploadedByUserId: string;
  uploadedByUserName?: string | null;
  createdAtUtc: string;
};

export type DocumentationAttachmentDownload = {
  attachmentId: string;
  fileName: string;
  url: string;
  expiresAtUtc: string;
};

const attachmentsBase = (pageId: string) => activeDocumentationApiTargets.attachmentsBase(pageId);

export async function uploadDocumentationAttachment(
  pageId: string,
  file: File,
): Promise<DocumentationAttachment> {
  const form = new FormData();
  form.append('file', file, file.name || 'imagem.png');
  const response = await apiClient.post<DocumentationAttachment>(attachmentsBase(pageId), form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
}

export type DocumentationDailyCount = {
  count: number;
};

export async function getDocumentationCount(): Promise<number> {
  const response = await apiClient.get<DocumentationDailyCount>('/v1/technology/documentation/count');
  return response.data.count;
}

export async function getDocumentationAttachmentDownloadUrl(
  pageId: string,
  attachmentId: string,
): Promise<DocumentationAttachmentDownload> {
  const response = await apiClient.get<DocumentationAttachmentDownload>(
    `${attachmentsBase(pageId)}/${attachmentId}/download-url`,
  );
  return response.data;
}
